/**
 * Canonical IR node kinds and canvas type strings.
 *
 * SINGLE SOURCE OF TRUTH for every structural type classification in the
 * Canvas rendering pipeline. Any `node.type === 'condition'` /
 * `nodeType === 'parallel_join'` etc. check in the codebase MUST go
 * through the predicates exported here. Raw string comparisons create the
 * drift (`switch` vs `condition` vs `decision` — all meant the same thing)
 * that motivated the IR design in the first place.
 *
 * See: docs/IR_RULE_MAPPING.md §2 (IR sketch), §9.1 (IR lives TS-only on
 * frontend), §7.1 (build order — this is step 1).
 *
 * Two distinct axes live here:
 *
 *  1. `IR_KIND` — structural role in the IR tree (9 values).
 *     Drives recursive layout. Used when building/consuming the IR.
 *
 *  2. `CANVAS_TYPE` — the `type` string that shows up on flat canvas
 *     nodes (pre- and post-flatten). This is what currently lives on
 *     `node.type`; keeping the string values stable preserves backward
 *     compatibility with every place in CanvasPage.jsx / useCanvas.js
 *     / dagLayout.js that hasn't been migrated yet.
 *
 * Migration is incremental: add the predicate, then over time replace
 * OR-chains with the predicate. No existing code is moved by this file.
 */

// ───────────────────────────────────────────────────────────────────
//  1. IR structural kinds (the 9 values that live in IR tree nodes)
// ───────────────────────────────────────────────────────────────────

/**
 * Structural kind of an IR node. Every IRNode in the tree has exactly one.
 *
 * @readonly
 * @enum {string}
 */
export const IR_KIND = Object.freeze({
  /** Scalar task — any non-container, non-sentinel, non-terminate. */
  LEAF:          'leaf',
  /** SWITCH / DECISION container with cases + optional default + optional convergence. */
  SWITCH:        'switch',
  /** FORK_JOIN container with static branches and an explicit JOIN. */
  FORK:          'fork',
  /** FORK_JOIN_DYNAMIC — runtime-resolved fan-out. Renders scalar at def time. */
  DYNAMIC_FORK:  'dynamicFork',
  /** DO_WHILE / FOR_EACH loop container with body + condition. */
  DO_WHILE:      'doWhile',
  /** SUB_WORKFLOW (sync) or START_WORKFLOW (async). `.async` flag distinguishes. */
  SUB_WORKFLOW:  'subWorkflow',
  /** TERMINATE — dead-end node with reason + status. */
  TERMINATE:     'terminate',
  /** Workflow entry sentinel. Renders as 80×80 circle. */
  START:         'start',
  /** Workflow exit sentinel. Renders as 80×80 circle. */
  END:           'end',
});

/** All valid IR kind strings. */
export const ALL_IR_KINDS = Object.freeze(Object.values(IR_KIND));

// ───────────────────────────────────────────────────────────────────
//  2. Canvas flat-node type strings (for `node.type`)
// ───────────────────────────────────────────────────────────────────

/**
 * The canonical `type` string for flat canvas nodes (pre- and post-flatten).
 *
 * These are the CANONICAL spellings. Legacy aliases that appear in the
 * codebase today (e.g. `'switch'`, `'decision'`, `'fork'`, `'join'`,
 * `'convergence'`, `'switch_join'`, `'do_while'`, `'for_each'`,
 * `'start_workflow'`, `'async_sub_workflow'`) all map to one of these via
 * {@link normalizeCanvasType}.
 *
 * @readonly
 * @enum {string}
 */
export const CANVAS_TYPE = Object.freeze({
  // ── Container / control-flow ────────────────────────────
  /** SWITCH / DECISION head — renders as 450×200 rounded rect. */
  CONDITION:        'condition',
  /** FORK_JOIN / FORK_JOIN_DYNAMIC head — 350×120. */
  PARALLEL_FORK:    'parallel_fork',
  /** JOIN / EXCLUSIVE_JOIN — 350×120. */
  PARALLEL_JOIN:    'parallel_join',
  /** DO_WHILE / FOR_EACH head — 350×130 with a 450×400+ frame around body. */
  LOOP:             'loop',
  /** SUB_WORKFLOW head — 350×130. `.config.async === true` ⇒ START_WORKFLOW. */
  SUB_WORKFLOW:     'sub_workflow',
  /** TERMINATE / TERMINATE_WORKFLOW — 350×90, no icon badge. */
  TERMINATE:        'terminate',
  /** Synthesized convergence pill for SWITCH. See Switch.convergence in IR. */
  CONVERGENCE:      'convergence',

  // ── Sentinels ────────────────────────────────────────────
  START:            'start',
  END:              'end',

  // ── Leaf (scalar) task sub-types ────────────────────────
  /**
   * @deprecated ADR-002 P1.1 (2026-05-31). Legacy catch-all that erases
   * semantic distinctions the matcher needs. Specific replacements:
   *   JDBC          → DATABASE_TASK
   *   UPDATE_SECRET → SECRET_WRITE_TASK
   *   GET_WORKFLOW / UPDATE_TASK keep TASK only until P5 of ADR-001.
   * New code MUST NOT emit 'task' — pick a specific canonical type.
   */
  TASK:                  'task',                  // DEPRECATED — see JSDoc above
  WORKER_TASK:           'worker_task',           // Conductor SIMPLE / customer-written polling worker
  HTTP_TASK:             'http_task',             // HTTP, GRPC, GET_SIGNED_JWT
  SCRIPT_TASK:           'script_task',           // INLINE, LAMBDA, BUSINESS_RULE
  TRANSFORM:             'transform',             // JSON_JQ_TRANSFORM
  DATA:                  'data',                  // SET_VARIABLE
  EVENT_PUBLISH:         'event_publish',         // KAFKA_PUBLISH
  NOTIFICATION_TASK:     'notification_task',     // EVENT
  DELAY:                 'delay',                 // WAIT, YIELD
  SIGNAL_WAIT:           'signal_wait',           // WAIT_FOR_WEBHOOK, WAIT_FOR_EVENT
  HUMAN_TASK:            'human_task',            // HUMAN — primary form-driven approval node (legacy 'approval_gate' aliases here)
  DYNAMIC_TASK:          'dynamic_task',          // DYNAMIC (single resolve-at-runtime)
  LLM_TASK:              'llm_task',              // LLM_TEXT_COMPLETE, LLM_CHAT_COMPLETE
  EMBEDDINGS_TASK:       'embeddings_task',       // LLM_GENERATE_EMBEDDINGS
  VECTOR_STORE_TASK:     'vector_store_task',     // LLM_STORE_EMBEDDINGS, LLM_INDEX_*
  VECTOR_SEARCH_TASK:    'vector_search_task',    // LLM_GET_EMBEDDINGS, LLM_SEARCH_INDEX
  CHUNK_TEXT_TASK:       'chunk_text_task',
  FILE_LIST_TASK:        'file_list_task',
  PARSE_DOCUMENT_TASK:   'parse_document_task',
  DOCUMENT_UPLOAD_TASK:  'document_upload_task',
  HTTP_POLL_TASK:        'http_poll_task',
  /**
   * @deprecated ADR-002 P1.1 (2026-05-31). Catch-all for SENDGRID /
   * OPSGENIE / generic state-mutating actions. Replaced by typed types:
   *   SENDGRID → EMAIL_TASK
   *   OPSGENIE / Slack / Teams / PagerDuty → NOTIFICATION_TASK with
   *     channel kind in node.config
   *   generic state-mutating → pick a specific verb
   *     (FILE_WRITE_TASK, DATABASE_TASK, etc.)
   * Continues to function through P5 of ADR-001.
   */
  ACTION:                'action',                // DEPRECATED — see JSDoc above

  // ═════════════════════════════════════════════════════════════════
  // ADR-002 P1.1 — Canonical Node-Type Coverage additions (2026-05-31).
  //
  // Fifteen new canvas type strings paired with the seventeen new
  // entries in backend CanonicalNodeType. DATABASE_QUERY +
  // DATABASE_MUTATION share 'database_task'; NOTIFICATION_DISPATCH uses
  // existing NOTIFICATION_TASK above. Strictly additive — adapters
  // and runtime branches follow in P3 of ADR-001. Until then no
  // producer emits these and no executor handles them, but the wire
  // contract is reserved so downstream layers can evolve independently.
  // ═════════════════════════════════════════════════════════════════
  DATABASE_TASK:           'database_task',
  RULE_TASK:               'rule_task',
  MESSAGE_CONSUME_TASK:    'message_consume_task',
  EMAIL_TASK:              'email_task',
  FORM_TASK:               'form_task',
  COMPENSATION_TASK:       'compensation_task',
  BATCH_TASK:              'batch_task',
  DATA_FILTER_TASK:        'data_filter_task',
  DATA_VALIDATE_TASK:      'data_validate_task',
  RPC_TASK:                'rpc_task',
  FILE_READ_TASK:          'file_read_task',
  FILE_WRITE_TASK:         'file_write_task',
  SECRET_READ_TASK:        'secret_read_task',
  SECRET_WRITE_TASK:       'secret_write_task',
  WEBHOOK_CALLBACK_TASK:   'webhook_callback_task',
});

/**
 * Canvas types deprecated by ADR-002 P1.1. Continue to render and
 * execute through P5 of ADR-001, but new producers MUST NOT emit them.
 * Useful for dev-tools / lint to flag legacy emissions, and for the
 * closure-invariant CI test to treat these as "allowed under
 * deprecation grace period."
 */
export const DEPRECATED_CANVAS_TYPES = Object.freeze(new Set([
  'task',
  'action',
]));

/** All valid canvas type strings. */
export const ALL_CANVAS_TYPES = Object.freeze(Object.values(CANVAS_TYPE));

// ───────────────────────────────────────────────────────────────────
//  3. Legacy alias resolution (the core drift fix)
// ───────────────────────────────────────────────────────────────────

/**
 * Every legacy alias found in the codebase today, mapped to its canonical
 * CANVAS_TYPE string. Verified against a 2026-04-23 full-tree grep.
 *
 * @type {Readonly<Object<string,string>>}
 */
const CANVAS_TYPE_ALIASES = Object.freeze({
  // SWITCH family
  switch:              CANVAS_TYPE.CONDITION,
  decision:            CANVAS_TYPE.CONDITION,
  switch_case:         CANVAS_TYPE.CONDITION,
  // FORK family
  fork:                CANVAS_TYPE.PARALLEL_FORK,
  fork_join:           CANVAS_TYPE.PARALLEL_FORK,
  fork_join_dynamic:   CANVAS_TYPE.PARALLEL_FORK,
  dynamic_fork:        CANVAS_TYPE.PARALLEL_FORK,
  // JOIN / convergence family
  join:                CANVAS_TYPE.PARALLEL_JOIN,
  switch_join:         CANVAS_TYPE.CONVERGENCE,
  exclusive_join:      CANVAS_TYPE.PARALLEL_JOIN,
  // LOOP family
  do_while:            CANVAS_TYPE.LOOP,
  for_each:            CANVAS_TYPE.LOOP,
  foreach:             CANVAS_TYPE.LOOP,
  // SUB_WORKFLOW family — async spellings preserved as hints, routed to sub_workflow
  start_workflow:      CANVAS_TYPE.SUB_WORKFLOW,
  async_sub_workflow:  CANVAS_TYPE.SUB_WORKFLOW,
  // TERMINATE family
  terminate_workflow:  CANVAS_TYPE.TERMINATE,
  // Sentinel aliases — actual canvas JSON uses `trigger` for Start
  trigger:             CANVAS_TYPE.START,
  manual_start:        CANVAS_TYPE.START,
  // SUB_WORKFLOW aliases — actual canvas JSON uses `agent`
  agent:               CANVAS_TYPE.SUB_WORKFLOW,
  // Leaf-task aliases observed in migration data
  script:              CANVAS_TYPE.SCRIPT_TASK,
  http:                CANVAS_TYPE.HTTP_TASK,
  // HUMAN-task family — approval_gate is the legacy spelling, both
  // resolve to HUMAN_TASK so layout/lint/IR treat them identically.
  approval_gate:       CANVAS_TYPE.HUMAN_TASK,
  approval:            CANVAS_TYPE.HUMAN_TASK,
});

/**
 * Resolve any legacy canvas-type string to its canonical form. Idempotent.
 * Unknown strings pass through unchanged (so a new type added somewhere
 * in the future won't silently become `TASK`).
 *
 * @param {string | null | undefined} rawType
 * @returns {string}  The canonical CANVAS_TYPE value, or the original if unknown.
 */
export function normalizeCanvasType(rawType) {
  if (rawType == null) return '';
  const lower = String(rawType).toLowerCase();
  return CANVAS_TYPE_ALIASES[lower] ?? lower;
}

// ───────────────────────────────────────────────────────────────────
//  3b. Loop handle alias resolution
// ───────────────────────────────────────────────────────────────────

/**
 * A loop node hands control to two places — into its body, and onward past
 * itself — and three different vocabularies name those two edges.
 *
 * <pre>
 *                          body handle      exit handle
 *   IR walker / irToFlat /  'default'        'exit'      <- CANONICAL
 *     routeEdges
 *   data/soarNodes.js       'body'           'done'      <- what the palette emits
 *   data/soarPlaybooks.js   'loopStart'      'loopComplete'
 * </pre>
 *
 * `default` / `exit` is canonical, and not by preference: `irToFlat` writes
 * `exitHandle: 'exit'`, `routeEdges` anchors the loop head's exit port on
 * `sourceHandle === 'exit'`, and every loop fixture in the IR suite uses the
 * pair. The other two vocabularies are producers that never matched their
 * consumer.
 *
 * The cost of the mismatch was total, not cosmetic. `buildDoWhile` selects
 * the body by the `default` handle and `advanceAfter` selects the exit by
 * `exit`, so a loop built from the palette had BOTH an empty body and no
 * successor: the walk stopped at the loop head and every node after it —
 * body, tail, and the End sentinel — vanished from the canvas while
 * remaining in the saved JSON.
 *
 * These sets exist so the walker can ACCEPT what the palette and the shipped
 * templates already produce. Converging the producers onto the canonical
 * spelling is a separate change; until it lands, workflows already saved in
 * the field still have to render.
 *
 * Deliberately scoped to loops rather than a general handle normalizer:
 * `body` and `done` are unremarkable words that mean other things elsewhere,
 * and a global rewrite would reach edges these sets have no business
 * touching.
 */
const LOOP_BODY_HANDLES = Object.freeze(new Set(['default', 'body', 'loopstart']));
const LOOP_EXIT_HANDLES = Object.freeze(new Set(['exit', 'done', 'loopcomplete']));

/** The canonical spelling of each, for producers and for tests. */
export const LOOP_BODY_HANDLE = 'default';
export const LOOP_EXIT_HANDLE = 'exit';

/** An absent handle means the default port, matching the rest of the walker. */
function normalizeHandle(rawHandle) {
  if (rawHandle == null || rawHandle === '') return 'default';
  return String(rawHandle).toLowerCase();
}

/**
 * True when this out-edge of a loop node leads into the loop BODY.
 * Accepts the canonical `default` plus the palette and playbook spellings.
 */
export function isLoopBodyHandle(rawHandle) {
  return LOOP_BODY_HANDLES.has(normalizeHandle(rawHandle));
}

/**
 * True when this out-edge of a loop node is the loop's EXIT — the edge the
 * enclosing sequence continues from once the loop finishes.
 */
export function isLoopExitHandle(rawHandle) {
  return LOOP_EXIT_HANDLES.has(normalizeHandle(rawHandle));
}

// ───────────────────────────────────────────────────────────────────
//  4. Predicates (replace every `nodeType === 'x' || nodeType === 'y'`)
// ───────────────────────────────────────────────────────────────────

/** True for SWITCH / DECISION / condition regardless of alias spelling. */
export function isSwitchType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.CONDITION;
}

/** True for any FORK head (static or dynamic). */
export function isForkType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.PARALLEL_FORK;
}

/**
 * True for any convergence / join node — PARALLEL_JOIN (FORK convergence)
 * OR CONVERGENCE (SWITCH catch pill). Callers that need to distinguish
 * the two should use {@link isForkJoinType} or {@link isSwitchJoinType}.
 */
export function isJoinType(rawType) {
  const c = normalizeCanvasType(rawType);
  return c === CANVAS_TYPE.PARALLEL_JOIN || c === CANVAS_TYPE.CONVERGENCE;
}

/** True specifically for FORK convergence. */
export function isForkJoinType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.PARALLEL_JOIN;
}

/** True specifically for SWITCH convergence catch pill. */
export function isSwitchJoinType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.CONVERGENCE;
}

/** True for DO_WHILE / FOR_EACH / FOREACH. */
export function isLoopType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.LOOP;
}

/** True for SUB_WORKFLOW / START_WORKFLOW (async). */
export function isSubWorkflowType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.SUB_WORKFLOW;
}

/** True for TERMINATE / TERMINATE_WORKFLOW. */
export function isTerminateType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.TERMINATE;
}

/** True for the workflow entry sentinel. */
export function isStartType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.START;
}

/**
 * True for any node type that can sit at the *head* of a workflow as
 * the trigger / entry point. Distinct from {@link isStartType}, which
 * is the canonical Start sentinel (a single node per workflow). A
 * trigger-capable node BECOMES the Start when it has no incoming
 * edges — e.g. a `webhook` with no upstream is the workflow's entry,
 * but a `webhook` downstream of a `manual_start` is just a leaf.
 *
 * Used by {@link findStart} (in flatToIR) as a tiebreaker when the
 * canvas has no explicit Start sentinel — common after the AI
 * Workflow Builder rewrites a workflow with a different trigger
 * shape (e.g. swapping manual_start for form_trigger). Without this
 * predicate, flatToIR throws "no Start sentinel" and the canvas
 * blanks out mid-edit.
 */
export function isTriggerCapableType(rawType) {
  if (rawType == null) return false;
  const lower = String(rawType).toLowerCase();
  // Includes the canonical Start aliases AND every trigger-style
  // entry point exposed by the palette. Keep this in sync with
  // soarNodes.js Triggers & Events category.
  return (
    lower === 'start'
    || lower === 'manual_start'
    || lower === 'trigger'
    || lower === 'webhook'
    || lower === 'schedule'
    || lower === 'siem_alert'
    || lower === 'email_trigger'
    || lower === 'system_event_trigger'
    || lower === 'form_trigger'
  );
}

/** True for the workflow exit sentinel. */
export function isEndType(rawType) {
  return normalizeCanvasType(rawType) === CANVAS_TYPE.END;
}

/** True for Start or End circle sentinels. */
export function isSentinelType(rawType) {
  return isStartType(rawType) || isEndType(rawType);
}

/** True if the type is a container (splits or collects flow). */
export function isContainerType(rawType) {
  const c = normalizeCanvasType(rawType);
  return c === CANVAS_TYPE.CONDITION
      || c === CANVAS_TYPE.PARALLEL_FORK
      || c === CANVAS_TYPE.LOOP;
}

// ───────────────────────────────────────────────────────────────────
//  5. IR_KIND ⇄ CANVAS_TYPE bridges
// ───────────────────────────────────────────────────────────────────

/**
 * Map a canvas `node.type` string to its IR structural kind. Used by
 * `flatToIR` (step 3 of the IR build) when reconstructing the tree from
 * the flat canvas.
 *
 * Returns {@link IR_KIND.LEAF} for any leaf / scalar sub-type, so callers
 * get `leaf` back for `http_task`, `script_task`, `transform`, etc.
 * Convergence is returned as `null` because it is a Switch child property,
 * not a first-class IR node.
 *
 * @param {string} rawType
 * @returns {string | null}
 */
export function canvasTypeToIrKind(rawType) {
  const c = normalizeCanvasType(rawType);
  switch (c) {
    case CANVAS_TYPE.CONDITION:       return IR_KIND.SWITCH;
    case CANVAS_TYPE.PARALLEL_FORK:   return IR_KIND.FORK;   // dynamic vs static flagged on the node
    case CANVAS_TYPE.PARALLEL_JOIN:   return IR_KIND.FORK;   // JOIN belongs to its FORK; absorbed at IR-build
    case CANVAS_TYPE.LOOP:            return IR_KIND.DO_WHILE;
    case CANVAS_TYPE.SUB_WORKFLOW:    return IR_KIND.SUB_WORKFLOW;
    case CANVAS_TYPE.TERMINATE:       return IR_KIND.TERMINATE;
    case CANVAS_TYPE.START:           return IR_KIND.START;
    case CANVAS_TYPE.END:             return IR_KIND.END;
    case CANVAS_TYPE.CONVERGENCE:     return null;  // lives on Switch.convergence, not a standalone node
    default:                          return IR_KIND.LEAF;
  }
}

/**
 * Map an IR kind (+ flags) back to its canonical canvas type string. Used
 * by `irToFlat` when flattening for render. Convergence is handled
 * separately since it is synthesized, not reduced from an IR node.
 *
 * @param {string} irKind  one of IR_KIND values
 * @param {{ dynamic?: boolean, async?: boolean }} [flags]
 * @returns {string}
 */
export function irKindToCanvasType(irKind, flags = {}) {
  switch (irKind) {
    case IR_KIND.LEAF:          return CANVAS_TYPE.TASK;  // caller should override with specific leaf type
    case IR_KIND.SWITCH:        return CANVAS_TYPE.CONDITION;
    case IR_KIND.FORK:
    case IR_KIND.DYNAMIC_FORK:  return CANVAS_TYPE.PARALLEL_FORK;
    case IR_KIND.DO_WHILE:      return CANVAS_TYPE.LOOP;
    case IR_KIND.SUB_WORKFLOW:  return CANVAS_TYPE.SUB_WORKFLOW;
    case IR_KIND.TERMINATE:     return CANVAS_TYPE.TERMINATE;
    case IR_KIND.START:         return CANVAS_TYPE.START;
    case IR_KIND.END:           return CANVAS_TYPE.END;
    default:
      throw new Error(`irKindToCanvasType: unknown IR kind "${irKind}"`);
  }
}

// ───────────────────────────────────────────────────────────────────
//  6. Development guard — validate a canvas type is known
// ───────────────────────────────────────────────────────────────────

/**
 * Returns true if `rawType` (after alias resolution) is a known canonical
 * canvas type. Useful as a dev-mode assertion when ingesting JSON.
 *
 * @param {string} rawType
 * @returns {boolean}
 */
export function isKnownCanvasType(rawType) {
  return ALL_CANVAS_TYPES.includes(normalizeCanvasType(rawType));
}
